import { createContext, useEffect, useReducer, useRef, useState } from 'react'

const DojoContext = createContext()
import Kalvium from '@kalvi-education/kalvium-sdk'
import { useLocation, useNavigate } from 'react-router-dom'

import PROCTORING_IMAGE_TYPE from '../enums/proctoringImageType'
import useFullScreen from '../hooks/useFullScreen/useFullScreen.js'
import useProctoring from '../hooks/useProctoring/useProctoring'
import DOJO_ACTION from '../pages/Dojo/enums/dojoAction'
import BELT_TEST_STATE from '../pages/Dojo/enums/dojoBeltTestStates'
import DOJO_EXERCISE_ACTION from '../pages/Dojo/enums/dojoExerciseAction'
import DOJO_MODE from '../pages/Dojo/enums/dojoMode'
import DOJO_OTP_VALID from '../pages/Dojo/enums/dojoOtpValid'
import { initialState, reducer } from '../reducers/dojoReducer'
import { b64toBlob } from '../utils/common'

const DojoProvider = ({ children }) => {
    const location = useLocation()
    const isDojo = location.pathname.includes('/dojo')
    const navigate = useNavigate()
    const [store, dispatch] = useReducer(reducer, initialState)
    const [isLoading, setIsLoading] = useState(false)
    const mode = store.mode
    const timerRef = useRef()
    const questionDetails = store.exerciseDetails?.exercise_info
        ? store.exerciseDetails?.exercise_info[0]?.question
        : {}
    const questionId = questionDetails ? questionDetails.id : null
    const exerciseId = store.exerciseDetails ? store.exerciseDetails?.id : null
    const {
        isVideoCaptureAvailable,
        isScreenCaptureAvailable,
        stopAVCapture,
        stopScreenCapture,
        attachOnCut,
        attachOnCopy,
        attachOnPaste,
        setCutEnabled,
        setCopyEnabled,
        setPasteEnabled,
        attachOnDragStart,
        attachOnDrop,
        setDragStartEnabled,
        setDropEnabled,
        attachOnWindowResize,
        attachOnWindowFocus,
        attachOnWindowBlur,
        attachOnAvImage,
        attachOnScreenImage,
        startAvCaptureTimer,
        startScreenCaptureTimer,
        stopAvCaptureTimer,
        stopScreenCaptureTimer,
        getBrowserName,
        getBrowserVersion,
        getOS,
        getOSVersion,
        getVideoMaxResolution,
        setEntireScreenShareEnabled,
    } = useProctoring()

    const { setIsFullScreenModeEnabled } = useFullScreen()

    const onCut = async content => {
        await Kalvium.services.dojo.storeBeltTest({
            action: DOJO_EXERCISE_ACTION.LOG,
            beltTestId: store.beltTestDetails.id,
            events: [
                {
                    event: 'cut',
                    extras: {
                        content: content,
                        user_ts: Math.round(new Date().getTime() / 1000),
                    },
                },
            ],
        })
    }
    const onCopy = async content => {
        await Kalvium.services.dojo.storeBeltTest({
            action: DOJO_EXERCISE_ACTION.LOG,
            beltTestId: store.beltTestDetails.id,
            events: [
                {
                    event: 'copy',
                    extras: {
                        content: content,
                        user_ts: Math.round(new Date().getTime() / 1000),
                    },
                },
            ],
        })
    }
    const onPaste = async content => {
        await Kalvium.services.dojo.storeBeltTest({
            action: DOJO_EXERCISE_ACTION.LOG,
            beltTestId: store.beltTestDetails.id,
            events: [
                {
                    event: 'paste',
                    extras: {
                        content: content,
                        user_ts: Math.round(new Date().getTime() / 1000),
                    },
                },
            ],
        })
    }
    const onDragStart = async content => {
        await Kalvium.services.dojo.storeBeltTest({
            action: DOJO_EXERCISE_ACTION.LOG,
            beltTestId: store.beltTestDetails.id,
            events: [
                {
                    event: 'dragStart',
                    extras: {
                        content: content,
                        user_ts: Math.round(new Date().getTime() / 1000),
                    },
                },
            ],
        })
    }
    const onDrop = async content => {
        await Kalvium.services.dojo.storeBeltTest({
            action: DOJO_EXERCISE_ACTION.LOG,
            beltTestId: store.beltTestDetails.id,
            events: [
                {
                    event: 'drop',
                    extras: {
                        content: content,
                        user_ts: Math.round(new Date().getTime() / 1000),
                    },
                },
            ],
        })
    }

    const onWindowResize = (() => {
        let timer
        return async content => {
            clearTimeout(timer)
            timer = setTimeout(async () => {
                await Kalvium.services.dojo.storeBeltTest({
                    action: DOJO_EXERCISE_ACTION.LOG,
                    beltTestId: store.beltTestDetails.id,
                    events: [
                        {
                            event: 'windowresize',
                            extras: {
                                content: content,
                                user_ts: Math.round(
                                    new Date().getTime() / 1000
                                ),
                            },
                        },
                    ],
                })
            }, 1000) // Debounce delay set to 1 second
        }
    })()

    const onWindowFocus = async () => {
        await Kalvium.services.dojo.storeBeltTest({
            action: DOJO_EXERCISE_ACTION.LOG,
            beltTestId: store.beltTestDetails.id,
            events: [
                {
                    event: 'windowfocus',
                    extras: {
                        user_ts: Math.round(new Date().getTime() / 1000),
                    },
                },
            ],
        })
    }
    const onWindowBlur = async () => {
        await Kalvium.services.dojo.storeBeltTest({
            action: DOJO_EXERCISE_ACTION.LOG,
            beltTestId: store.beltTestDetails.id,
            events: [
                {
                    event: 'windowblur',
                    extras: {
                        user_ts: Math.round(new Date().getTime() / 1000),
                    },
                },
            ],
        })
    }

    const uploadProctoringImage = async ({
        type,
        imgData,
        beltTestId,
        fileName,
    }) => {
        try {
            const blob = b64toBlob(
                imgData.replace('data:image/jpeg;base64,', ''),
                'image/jpeg'
            )
            const file = new File([blob], { type: 'image/jpeg' })
            const data = {
                beltTestId,
                action:
                    type === PROCTORING_IMAGE_TYPE.SCREEN
                        ? 'upload_screenshot'
                        : 'upload_camera_image',
                params: { filename: fileName },
                file: file,
            }

            await Kalvium.services.dojo.uploadFile(data)
        } catch (error) {
            console.error(error)
        }
    }

    const onAvImage = imgData => {
        uploadProctoringImage({
            type: PROCTORING_IMAGE_TYPE.WEBCAM,
            imgData,
            beltTestId: store.beltTestDetails.id,
            fileName:
                `${getOS()}-${getOSVersion()}-${getBrowserName()}-${getBrowserVersion()}-${
                    getVideoMaxResolution().width
                }x${getVideoMaxResolution().height}.jpg`.toLowerCase(),
        })
    }
    const onScreenImage = imgData => {
        uploadProctoringImage({
            type: PROCTORING_IMAGE_TYPE.SCREEN,
            imgData,
            beltTestId: store.beltTestDetails.id,
            fileName:
                `${getOS()}-${getOSVersion()}-${getBrowserName()}-${getBrowserVersion()}-${
                    window.screen.width
                }x${window.screen.height}.jpg`.toLowerCase(),
        })
    }

    const startProctoring = () => {
        setCutEnabled(false)
        setCopyEnabled(false)
        setPasteEnabled(false)
        setDragStartEnabled(false)
        setDropEnabled(false)
        setIsFullScreenModeEnabled(true)
        attachOnCut(onCut)
        attachOnCopy(onCopy)
        attachOnPaste(onPaste)
        attachOnDragStart(onDragStart)
        attachOnDrop(onDrop)
        attachOnWindowResize(onWindowResize)
        attachOnWindowFocus(onWindowFocus)
        attachOnWindowBlur(onWindowBlur)
        attachOnAvImage(onAvImage)
        attachOnScreenImage(onScreenImage)
        startAvCaptureTimer(import.meta.env.VITE_BELT_TEST_AUTOSAVE_INTERVAL)
        startScreenCaptureTimer(
            import.meta.env.VITE_BELT_TEST_AUTOSAVE_INTERVAL
        )
    }

    const resetProctoringHandlers = () => {
        attachOnCut(undefined)
        attachOnCopy(undefined)
        attachOnPaste(undefined)
        attachOnDragStart(undefined)
        attachOnDrop(undefined)
        attachOnWindowResize(undefined)
        attachOnWindowFocus(undefined)
        attachOnWindowBlur(undefined)
        attachOnAvImage(undefined)
        attachOnScreenImage(undefined)
        stopAvCaptureTimer()
        stopScreenCaptureTimer()
        setCutEnabled(true)
        setCopyEnabled(true)
        setPasteEnabled(true)
        setDragStartEnabled(true)
        setDropEnabled(true)
        setIsFullScreenModeEnabled(false)
    }

    useEffect(() => {
        let testEndTimer
        const startBeltTest = () => {
            testEndTimer = setInterval(() => {
                dispatch({ type: DOJO_ACTION.DECREMENT_REM_TIME })
            }, 1000)
            timerRef.current = testEndTimer
        }

        if (store.beltTestState === BELT_TEST_STATE.RUNNING) {
            startBeltTest()
        } else if (store.beltTestState === BELT_TEST_STATE.CLOSED) {
            clearTimers()
            stopAVCapture()
            stopScreenCapture()
            resetProctoringHandlers()
        }
        return () => {
            testEndTimer && clearInterval(testEndTimer)
        }
    }, [store.beltTestState])

    const clearTimers = () => {
        timerRef.current && clearInterval(timerRef.current)
    }

    // Leaving for Workout must not close the belt test, so toggling back resumes
    // the same attempt instead of resetting lives/time (KAL-4885).
    const switchToWorkoutMode = async () => {
        try {
            setIsLoading(true)
            stopAVCapture()
            stopScreenCapture()
            resetProctoringHandlers()
            dispatch({
                type: DOJO_ACTION.SET_MODE,
                payload:
                    mode === DOJO_MODE.WORKOUT
                        ? DOJO_MODE.BELT
                        : DOJO_MODE.WORKOUT,
            })
            dispatch({
                type: DOJO_ACTION.SET_OTP_VALIDITY,
                payload: DOJO_OTP_VALID.NOT_CHANGED,
            })
            dispatch({
                type: DOJO_ACTION.SET_BELT_TEST_STATE,
                payload: BELT_TEST_STATE.CLOSED,
            })
            return true
        } catch (error) {
            console.log(error)
            return false
        } finally {
            setIsLoading(false)
        }
    }

    const submitExercise = async () => {
        try {
            setIsLoading(true)
            await Kalvium.services.dojo.saveExercise({
                action: DOJO_EXERCISE_ACTION.SUBMIT,
                exerciseId,
                questions: [
                    {
                        question_id: questionId,
                        user_solution:
                            store.localUserSolutionData[questionId]
                                .user_solution,
                    },
                ],
            })

            return true
        } catch (error) {
            console.log(error.message)
            return false
        } finally {
            setIsLoading(false)
        }
    }

    const createBeltTest = async value => {
        try {
            setIsLoading(true)
            const createBeltTest = await Kalvium.services.dojo.createBeltTest(
                value
            )
            dispatch({
                type: DOJO_ACTION.SET_BELT_TEST_DETAILS,
                payload: {
                    beltTestDetails: createBeltTest,
                    remTime: createBeltTest.remaining_time,
                    otpValidity: DOJO_OTP_VALID.VALID,
                    beltTestState: BELT_TEST_STATE.RUNNING,
                },
            })
            return true
        } catch (error) {
            console.log(error.message)
            dispatch({
                type: DOJO_ACTION.SET_OTP_VALIDITY,
                payload: DOJO_OTP_VALID.INVALID,
            })
            return false
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (isDojo) {
            if (mode === DOJO_MODE.BELT) startProctoring()
            setEntireScreenShareEnabled(true)
            setCutEnabled(false)
            setCopyEnabled(false)
            setPasteEnabled(false)
            setDragStartEnabled(false)
            setDropEnabled(false)
        }
        return () => {
            setEntireScreenShareEnabled(false)
            setCutEnabled(true)
            setCopyEnabled(true)
            setPasteEnabled(true)
            setDragStartEnabled(true)
            setDropEnabled(true)
        }
    }, [mode, isDojo])

    useEffect(() => {
        if (
            store.beltTestState === BELT_TEST_STATE.RUNNING &&
            (!isScreenCaptureAvailable || !isVideoCaptureAvailable) &&
            store.remTime > 0
        ) {
            dispatch({
                type: DOJO_ACTION.SET_MODE,
                payload: DOJO_MODE.WORKOUT,
            })
            dispatch({
                type: DOJO_ACTION.SET_BELT_TEST_STATE,
                payload: BELT_TEST_STATE.INITIAL,
            })
            stopAVCapture()
            stopScreenCapture()
            resetProctoringHandlers()
            clearTimers()
            navigate('/dojo')
        }
    }, [isVideoCaptureAvailable, isScreenCaptureAvailable, store.remTime])

    useEffect(() => {
        if (store.remTime <= 0) {
            stopAVCapture()
            stopScreenCapture()
            resetProctoringHandlers()
            clearTimers()
        }
    }, [store.remTime])

    return (
        <DojoContext.Provider
            value={{
                store,
                dispatch,
                switchToWorkoutMode,
                submitExercise,
                createBeltTest,
                isLoading,
                resetProctoringHandlers,
            }}
        >
            {children}
        </DojoContext.Provider>
    )
}

export default DojoContext
export { DojoProvider }
